import {
	ApplicationRef,
	ComponentFactoryResolver,
	EmbeddedViewRef,
	Injectable,
	Injector,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import AlephBet from 'alephbet';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { HotjarService } from 'app/services/hotjar.service';
import { environment } from 'environments/environment';
import { UnleashService } from 'shared/unleash/unleash.service';

import { UserService } from './../../../app/services/user.service';
import { ExperimentControlsWidgetComponent } from '../components/experiment-controls-widget.component';
import {
	IAlephBetExperiment,
	IAlephBetVariants,
	ITrackerScopes,
} from '../interfaces/experiment.interface';
import { AlephbetAdapter } from '../utils/AlephbetAdapter';
import { experiments } from '../utils/Experiments';

@Injectable({ providedIn: 'root' })
export class ABExperimentsService {
	@ViewChild('widgetContainer', { read: ViewContainerRef }) container;
	runningExperiments: IAlephBetExperiment[] = [];
	runningGoals = [];

	constructor(
		private unleashService: UnleashService,
		private ATService: ActionTrackerService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private appRef: ApplicationRef,
		private injector: Injector,
		private hotjarService: HotjarService,
		private userService: UserService
	) {
		//Se ambiente for localhost habilita o debug mode
		if (window.location.hostname.includes('localhost')) {
			AlephBet.options.debug = true;
		}
	}

	private createExperiment(
		name: string,
		variants: IAlephBetVariants,
		trackerScope: ITrackerScopes
	) {
		const _variants = {};
		//Mergeia o objeto de variantes customizado com o objeto de variantes padrão
		for (const key in variants) {
			if (Object.prototype.hasOwnProperty.call(variants, key)) {
				const element = variants[key];

				_variants[key] = {
					...element,
					activate: (experiment) => {
						this.defaultActivate(experiment, key, element.activate);
					},
				};
			}
		}

		//Cria o adapter para o GA4
		const adapter = new AlephbetAdapter(this.ATService, trackerScope);

		//Cria o experimento
		return new AlephBet.Experiment({
			name: name,
			tracking_adapter: {
				experiment_start: (experiment, variant) => {
					return adapter.experiment_start(experiment, variant);
				},
				goal_complete: (experiment, variant) => {
					return adapter.goal_complete(experiment, variant);
				},
			},
			variants: _variants,
		});
	}

	//Mescla o metodo de ativação padrão com o callback do usuário
	private defaultActivate(experiment, variant, callback) {
		this._log(
			`%c- [Experiment] Experiment Created: ${experiment.name}\n- Current variant: ${variant}`,
			experiment
		);
		// Adiciona o experimento criado no array de experimentos ativos
		this.runningExperiments.push({
			...experiment,
			currentVariant: variant,
		});

		callback?.();
	}

	//Retorna experimento do array de experimentos ativos
	getExperiment(experimentName): IAlephBetExperiment {
		const experiment = this.runningExperiments.find(
			(experiment) => experiment.name === experimentName
		);

		return experiment;
	}

	getAllExperiments = () => this.runningExperiments;

	//Cria um objetivo
	private createGoal(name, unique) {
		const goal = new AlephBet.Goal(name, { unique });
		this.runningGoals.push(goal);
		this._log(`%c[Experiment] Goal created: ${name}`, goal);
		return goal;
	}

	//Completa objetivo
	public completeGoal(goalName) {
		const _goal = this.runningGoals.find((goal) => goal.name === goalName);
		if (_goal) {
			_goal.complete();
			this._log(`%c[Experiment] Goal completed: ${_goal}`);
		}
	}

	//Loga o experimento apenas nos ambientes de desenvolvimento
	private _log(logName, logExtra?) {
		if (!environment.production) {
			console.log(
				logName,
				'color:darkred; font-size:12px; font-weight: 700; padding:10px;',
				logExtra || ''
			);
		}
	}

	// Força o uso de uma variante em um experimento **restrito a ambientes de desenvolvimento**
	public forceUseVariantOnExperient(experimentName, variantName) {
		if (!environment.production) {
			const experiment = this.getExperiment(experimentName);

			this.runningExperiments = this.runningExperiments.map((experiment) => {
				if (experiment.name === experimentName) {
					return { ...experiment, currentVariant: variantName };
				}
				return experiment;
			});
			const alephbet = JSON.parse(localStorage.getItem('alephbet'));
			alephbet[experimentName] = variantName;
			localStorage.setItem('alephbet', JSON.stringify(alephbet));
			this._log(
				`%c[Experiment]  Forcing variant "${variantName}" use on ${experimentName}`
			);
			return experiment;
		}
	}

	public async startAllExperiments() {
		const userId = await this.userService.getUserId();
		experiments.forEach((experiment) => {
			//Transforma variante local no tipo aceitado pelo Allephbet
			const _variants = experiment.variants.reduce(
				(acc, cur) => ({ ...acc, [cur.name]: { ...cur } }),
				{}
			);
			// Inicia experimentos que estiverem ativos no unleash
			if (this.unleashService.isEnabled(experiment.name)) {
				const _experiment = this.createExperiment(
					experiment.name,
					_variants,
					experiment.trackers
				);

				_experiment.add_goals(
					experiment.goals.map((goal) => {
						return this.createGoal(goal, false);
					})
				);
			}

			this.hotjarService.identifyUser(userId, {
				runningExperiments: this.runningExperiments
					.map(
						(experiment) => `${experiment.name}:${experiment.currentVariant}`
					)
					.join(' '),
			});
		});

		//Renderiza os controles dos experimentos para os ambientes de desenvolvimento
		if (this.runningExperiments.length > 0 && !environment.production) {
			this.renderDevelopmentControls();
		}
	}

	//Retorna variante pelo nome do experimento

	public getVariantByExperimentName(experimentName) {
		const alephbet = JSON.parse(localStorage.getItem('alephbet'));
		return alephbet[experimentName];
	}

	private renderDevelopmentControls() {
		const componentRef = this.componentFactoryResolver
			.resolveComponentFactory(ExperimentControlsWidgetComponent)
			.create(this.injector);

		this.appRef.attachView(componentRef.hostView);

		const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
			.rootNodes[0] as HTMLElement;

		document.body.appendChild(domElem);
	}
}
